import { forwardRef, useState } from 'react';
import type { PropsWithoutRef } from 'react';

import { Flex, Icon, registerIcon, Text, useI18n } from '@pega/cosmos-react-core';
import { AISummary } from '@pega/cosmos-react-social';
import type { ForwardRefForwardPropsComponent } from '@pega/cosmos-react-core';
import * as mailIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/mail.icon';

import type { EmailShellProps } from './Email.types';
import EmailConversation, {
  StyledEmailConversation,
  StyledEmailInConversation,
  StyledUnReadCount
} from './EmailConversation';
import {
  StyledEmailConversationList,
  StyledEmailShell,
  StyledEmailShellHeader
} from './Email.styles';

registerIcon(mailIcon);

const EmailShell: ForwardRefForwardPropsComponent<EmailShellProps> = forwardRef(function EmailShell(
  props: PropsWithoutRef<EmailShellProps>,
  ref: EmailShellProps['ref']
) {
  const { conversations, headerProps, autoCollapse = false, summary, ...restProps } = props;

  const t = useI18n();

  const [activeIndex, setActiveIndex] = useState(0);

  const singleConversation = conversations?.length === 1;

  const renderConversations = () => {
    if (!conversations) return;

    if (singleConversation) {
      const conversation = conversations[0];
      return (
        <StyledEmailConversation singleConversation={singleConversation} showHeader={!!headerProps}>
          {!!conversation.unReadEmailCount && (
            <Text as={StyledUnReadCount} variant='secondary'>
              {t('new_emails_count', [conversation.unReadEmailCount], {
                count: conversation.unReadEmailCount
              })}
            </Text>
          )}
          {conversation.emails?.map(email => {
            return <StyledEmailInConversation key={email.id} {...email} />;
          })}
        </StyledEmailConversation>
      );
    }

    // Multiple conversations
    return autoCollapse
      ? conversations?.map((conversation, index) => {
          const { onExpand, isCollapsed, ...rest } = conversation;
          const collapsed = index !== activeIndex;
          return (
            <EmailConversation
              key={conversation.id}
              onExpand={() => {
                setActiveIndex(index);
                if (onExpand) onExpand();
              }}
              isCollapsed={collapsed}
              {...rest}
            />
          );
        })
      : conversations?.map(conversation => {
          return <EmailConversation key={conversation.id} {...conversation} />;
        });
  };

  return (
    <Flex
      {...restProps}
      as={StyledEmailShell}
      container={{ direction: 'column', gap: singleConversation ? undefined : 1 }}
      ref={ref}
      singleConversation={singleConversation}
    >
      {(headerProps || summary) && (
        <Flex container={{ direction: 'column', gap: 0.5 }} as={StyledEmailShellHeader}>
          {headerProps && (
            <Flex
              container={{
                alignItems: 'center',
                justify: 'between',
                pad: 1
              }}
            >
              <Flex container={{ gap: 1, alignItems: 'center' }}>
                <Icon name='mail' />
                <Text variant='h3'>
                  {headerProps.subject ? `${t('subject')}: ${headerProps.subject} ` : t('email')}
                </Text>
              </Flex>
              {headerProps.actions}
            </Flex>
          )}

          {summary && <AISummary {...summary} />}
        </Flex>
      )}

      <StyledEmailConversationList>{renderConversations()}</StyledEmailConversationList>
    </Flex>
  );
});

export default EmailShell;
